// A page bundle as a file the browser hands over, and back.
//
// Two functions and no component, because both halves of this are DOM work
// rather than rendering: there is no element on the screen that is the file.
// Kept out of the screens so the suite can drive them without a document —
// which is also the only way to test the download at all, since jsdom has no
// downloads.

// What the file is called.
//
// The route rather than the title, because a route is already URL-safe and is
// the thing that identifies a page to the person who will open the file; the
// locale, because a group exports once per source language and two files called
// `pricing.json` in a downloads folder is a filename that lost the argument.
// The pageId is not in it: it is a uuid, it is INSIDE the file, and a name
// nobody can read is a name nobody can sort.
export const bundleFilename = (page) => {
  const route = String((page && page.route) || "")
    .replace(/^\/+|\/+$/g, "")
    .replace(/[^a-zA-Z0-9-]+/g, "-");
  const locale = String((page && page.locale) || "").replace(/[^a-zA-Z0-9-]+/g, "-");

  // `filter(Boolean)` is what makes the locale optional: a page with none — the
  // ordinary single-language site — is `page.pricing.json` rather than
  // `page.pricing..json`.
  return ["page", route || (page && page.pageId) || "bundle", locale, "json"]
    .filter(Boolean)
    .join(".");
};

// Two spaces and a trailing newline, because the point of this format is that
// somebody edits it: minified JSON is a file with one line, and a one-line diff
// of a one-line file says nothing about what changed in it.
export const bundleText = (bundle) => `${JSON.stringify(bundle, null, 2)}\n`;

// Hand the file to the browser.
//
// An object URL and a synthetic click, which is the only way a page can produce
// a file without a server to serve it from — and it is REVOKED, because the URL
// pins the blob in memory for the life of the document otherwise.
export const downloadBundle = (bundle, filename, target = document) => {
  const url = URL.createObjectURL(
    new Blob([bundleText(bundle)], { type: "application/json" })
  );
  const link = target.createElement("a");

  link.href = url;
  link.download = filename;
  target.body.appendChild(link);
  link.click();
  target.body.removeChild(link);
  URL.revokeObjectURL(url);

  return url;
};

// What a file dropped on the import screen has to be before anything is sent.
//
// The server refuses all of this too and refuses it better — it knows which
// site it is — so this is not validation, it is the difference between "that is
// not a page bundle" now and a round trip that returns `page.import.invalid`.
// A file that gets past this is one the server's own answer is worth reading.
export const parseBundle = (text) => {
  let bundle;

  try {
    bundle = JSON.parse(text);
  } catch (e) {
    throw new Error("That file is not JSON.");
  }

  if (!bundle || typeof bundle !== "object" || Array.isArray(bundle)) {
    throw new Error("That file is not a page bundle.");
  }

  if (!Array.isArray(bundle.pages) || bundle.pages.length === 0) {
    throw new Error("That file carries no pages.");
  }

  return bundle;
};

// The file's text, as a promise.
//
// `FileReader` rather than `Blob.text()`, which is newer than the browsers this
// app supports and newer than the jsdom the suite runs in — a screen that works
// everywhere except under test is a screen with no test.
export const readFileText = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.readAsText(file);
  });
